import React, { useState, useEffect } from 'react';
import {
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LineChart,
  Line,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import { transactionsGraph } from '@/services/transactions/api';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useAuth } from '@/contexts/auth';

export default function MonthlyOverview() {
  const { currentWorkspace } = useAuth();

  const width = 1400;
  const height = 350;
  const colors = ['#E16408', '#00ABAB', '#263238', '#788195'];

  const [datas, setDatas] = useState([]);

  const [high, setHigh] = useState(0);
  const [low, setLow] = useState(0);
  const [unknown, setUnknown] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getTransactions = async () => {
      const data = await transactionsGraph(currentWorkspace);

      let dataArray = [];
      const withHours = data.with_hours;
      delete data.with_hours;

      Object.keys(data).forEach(item => {
        const temp = {
          name: convertDateFromIso(item, withHours),
          low: data[item].low,
          high: data[item].high,
          unknown: data[item].unknown,
        };
        dataArray.push(temp);
      });
      setDatas([...dataArray]);
      composeRates(dataArray);
      setLoading(false);
    };

    getTransactions().catch(console.error);
  }, []);

  const convertDateFromIso = (dateStr, withHours) => {
    const d = new Date(dateStr);

    let options;

    if (withHours) {
      options = {
        month: 'short',
        day: '2-digit',
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC', 
      };
    } else {
      options = {
        month: 'short',
        day: '2-digit',
        timeZone: 'UTC', 
      };
    }

    return new Intl.DateTimeFormat('en-US', options).format(d);
  };

  const composeRates = arr => {
    for (let item in arr) {
      if (arr[item].high > 0) {
        const temp = { ...high, total: arr[item].high };
        setHigh(temp);
      }
      if (item.low > 0) setLow({ ...low, total: low.total + item.low });
      if (item.unknown > 0)
        setUnknown({ ...unknown, total: unknown.total + item.unknown });
    }
  };

  const getRate = () => {
    const lowTotal = datas.reduce((sum, current) => sum + current.low, 0);
    const highTotal = datas.reduce((sum, current) => sum + current.high, 0);
    const unknownTotal = datas.reduce(
      (sum, current) => sum + current.unknown,
      0,
    );
    return [
      {
        name: 'High',
        total: highTotal,
      },
      {
        name: 'Low',
        total: lowTotal,
      },
      {
        name: 'Unknown',
        total: unknownTotal,
      },
    ];
  };

  const renderRate = (item, key) => {
    return (
      <div
        key={key}
        className="flex-col flex py-3 px-5 items-center justify-center border-dotted border-r-2 border-gray-200 last:border-0"
      >
        <h2
          className="capitalize  text-xl"
          style={{ color: '#6A6A6A' }}
        >
          {item.name}
        </h2>
        <span className="text-xl">{item.total}</span>
      </div>
    );
  };

  if (loading) {
    const antIcon = (
      <LoadingOutlined
        style={{ fontSize: 24 }}
        spin
      />
    );
    return <Spin indicator={antIcon} />;
  }

  return (
    <div>
      <div className="flex items-center justify-start">
        {getRate().map((item, key) => renderRate(item, key))}
      </div>
      <ResponsiveContainer
        width="100%"
        height={400}
      >
        <LineChart
          width={width}
          height={height}
          data={datas}
          margin={{ top: 50, right: 10, left: 10, bottom: 5 }}
        >
          <YAxis stroke={colors[3]} />
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="name"
            stroke={colors[3]}
            padding={{ left: 40, right: 40 }}
          />
          <Tooltip />
          <Legend
            iconType="circle"
            style={{ top: 50, marginLeft: 50 }}
          />
          <Line
            type="monotone"
            dataKey="low"
            stackId="day"
            activeDot={{ r: 8 }}
            fill={colors[1]}
            stroke={colors[1]}
            minPointSize={1}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="high"
            stackId="day"
            activeDot={{ r: 8 }}
            fill={colors[0]}
            stroke={colors[0]}
            minPointSize={1}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="unknown"
            stackId="day"
            activeDot={{ r: 8 }}
            fill={colors[2]}
            stroke={colors[2]}
            minPointSize={1}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
